import * as XLSX from 'xlsx';

export interface ExcelRow {
  businessIdentifier: string;
  folio: string;
  amount?: number;
  subType?: string;
  dueDate?: string;
  stakeholderIdentifier?: string;
  errorReason?: string;
}

export const DOCUMENT_SUB_TYPE_MAP: Record<string, string> = {
  '33': 'Factura Afecta',
  '34': 'Factura Exenta',
  '46': 'Factura de compra',
};

const DOCUMENT_TYPE_SYNONYMS: Record<string, string[]> = {
  '33': [
    'factura electronica afecta',
    'factura afecta',
    'factura afecta con iva',
    'factura con iva',
  ],
  '34': [
    'factura electronica exenta',
    'factura electronica excenta',
    'factura exenta',
    'factura excenta',
    'factura sin iva',
  ],
  '46': [
    'factura de compra',
    'factura compra',
    'factura electronica de compra',
  ],
};

const normalizeDocumentType = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const NORMALIZED_DOCUMENT_TYPE_SYNONYMS = Object.entries(
  DOCUMENT_TYPE_SYNONYMS,
).reduce<Map<string, string>>((acc, [subType, variants]) => {
  variants.forEach((variant) => {
    acc.set(normalizeDocumentType(variant), subType);
  });
  return acc;
}, new Map());

export const mapDocumentSubtype = (rawValue: unknown): string => {
  if (rawValue == null) return '46';

  const normalized = normalizeDocumentType(String(rawValue));

  if (/^\d{2}$/.test(normalized) && DOCUMENT_SUB_TYPE_MAP[normalized]) {
    return normalized;
  }

  if (!normalized) return '46';

  const directMatch = NORMALIZED_DOCUMENT_TYPE_SYNONYMS.get(normalized);
  if (directMatch) return directMatch;

  if (normalized.includes('compra')) return '46';
  if (normalized.includes('exent')) return '34';
  if (normalized.includes('sin iva')) return '34';
  if (normalized.includes('afect') || normalized.includes('iva')) return '33';

  return '46';
};

export const parseDueDate = (rawValue: unknown): string => {
  if (!rawValue) return '';

  if (typeof rawValue === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + rawValue * 86400000);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  if (rawValue instanceof Date && !Number.isNaN(rawValue.getTime())) {
    const year = rawValue.getFullYear();
    const month = String(rawValue.getMonth() + 1).padStart(2, '0');
    const day = String(rawValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const value = String(rawValue).trim();
  if (!value) return '';

  const normalized = value.replace(/\./g, '/').replace(/-/g, '/');
  const match = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);

  if (!match) {
    return value;
  }

  const [, dayStr, monthStr, yearStr] = match;
  const day = Number(dayStr);
  const month = Number(monthStr);
  let year = Number(yearStr);

  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
    return value;
  }

  if (yearStr.length === 2) {
    year += 2000;
  }

  if (day < 1 || day > 31 || month < 1 || month > 12) {
    return value;
  }

  const pad = (n: number) => n.toString().padStart(2, '0');

  return `${year.toString().padStart(4, '0')}-${pad(month)}-${pad(day)}`;
};

const normalizeRut = (rut?: string) => {
  if (!rut) return '';
  return rut.replace(/-k$/i, '-K');
};

export const parseExcelFile = async (file: File): Promise<ExcelRow[]> => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(data), {
    type: 'array',
    cellDates: false,
    cellNF: true,
  });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];

  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, {
    defval: '',
    raw: true,
  });

  return json.map((row) => {
    const subType = mapDocumentSubtype(
      row['Tipo documento'] ||
        row['Tipo Documento'] ||
        row['tipo documento'] ||
        row['Tipo'],
    );
    const dueDateRaw =
      row['Vencimiento'] ||
      row['Fecha Vencimiento'] ||
      row['Fecha vencimiento'] ||
      row['fecha vencimiento'] ||
      '';

    const stakeholderIdentifierRaw =
      row['Rut Deudor'] ?? row['Rut deudor'] ?? row['rut deudor'] ?? '';

    return {
      businessIdentifier: normalizeRut(
        String(
          row['Rut cliente'] ??
            row['Rut Cliente'] ??
            row['rut cliente'] ??
            row['RUT'] ??
            row['rut'] ??
            '',
        ),
      ),
      folio: String(row['Folio'] ?? row['folio'] ?? ''),
      subType,
      amount:
        Number(row['Monto']) ||
        Number(row['amount']) ||
        Number(row['Amount']) ||
        0,
      dueDate: parseDueDate(dueDateRaw),
      stakeholderIdentifier: normalizeRut(String(stakeholderIdentifierRaw)),
    };
  });
};
